import { useEffect, useMemo, useRef, useState } from "react";
import { ResultContainer, ResultText, Situation, SituationBg, SituationTitle, TopBanner, Type, TypeImg, TypeTitle, ResultContainerTop, LeadContainer, SituationText, TypeImgContainer } from "./BehaviourAnalysisResult.style";
import { types } from "../BehaviourAnalysis/BehaviourAnalysis";
import LeadModal from "./components/LeadModal";
import { fetchBloomreachCatalogItem, postBloomreachAnalysisLead } from "../../utils/api";
import { BloomreachCatalogItem } from "../../types/BloomreachCatalogItem";
import { BloomreachAnalysisLeadModel } from "../../types/BloomreachAnalysisLeadModel";
import { getCookie } from "../../utils/cookieService";
import Analytics from "../../utils/analytics";
import ThankYou from "./components/ThankYou";

type AnalysisLeadData = Pick<BloomreachAnalysisLeadModel, "firstName" | "email" | "phone">;

export default function BehaviourAnalysisResult() {
    const [situationFromUrl, setSituationFromUrl] = useState<string | null>(null);
    const [resultTypeFromUrl, setResultTypeFromUrl] = useState<types | undefined>();
    const [mappedResultType, setMappedResultType] = useState<string | undefined>();
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const [bloomreachItem, setBloomreachItem] = useState<BloomreachCatalogItem | null>(null);
    const [showLeadModal, setShowLeadModal] = useState<boolean>(true);
    const [leadSubmittedFromMini, setLeadSubmittedFromMini] = useState<boolean>(false);
    const leadContainerRef = useRef<HTMLDivElement | null>(null);

    //Skip the lead modal if the lead was already captured upstream (e.g. BehaviourAnalysisMini)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("leadSubmitted") === "true") {
            setShowLeadModal(false);
            setLeadSubmittedFromMini(true);
        }
    }, []);
  
    const typeMaping = {
    "tryghed": "tryghed",
    "struktur": "struktur",
    "relation": "relationer",
    "balance": "balance",
    "fremdrift": "fremdrift"
    }

    useEffect(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
       
        const situation = urlParams.get("situation");
        setSituationFromUrl(situation);
       
        const resultType = urlParams.get("resultType") as types;
        setResultTypeFromUrl(resultType);
        const mappedResultType = resultType ? typeMaping[resultType as keyof typeof typeMaping] : undefined;
        setMappedResultType(mappedResultType);
        
    }, []);

     //Get content for situation 
    useEffect(() => {
        const fetchbloomreachData = async () => {
            if (!resultTypeFromUrl || !situationFromUrl) {
                return;
            }

            try {   
                const bloomreachData = await fetchBloomreachCatalogItem(resultTypeFromUrl.toString(), situationFromUrl)
                setBloomreachItem(bloomreachData.data);
            } catch (err) {} 
        } 

        fetchbloomreachData();
    }, [resultTypeFromUrl, situationFromUrl]);

    //Get image url based on result type
    const url = useMemo(() => {
        switch (resultTypeFromUrl) {
            case types.tryghed:
                return "/assets/images/tryghed.svg";
            case types.balance:
                return "/assets/images/balance.svg";
            case types.relation:
                return "/assets/images/relation.svg";
            case types.struktur:
                return "/assets/images/struktur.svg";
            case types.fremdrift:
                return "/assets/images/fremdrift.svg";
            default:
                return "";
        }
    }, [resultTypeFromUrl]);

    //Check for window size
    useEffect(() => {
    if(window.innerWidth > 750) {
        setIsDesktop(true)
        } else {
        setIsDesktop(false)
        }
    }, []);
    
    const handleResize = () => {
        if(window.innerWidth > 750) {
        setIsDesktop(true)
        } else {
        setIsDesktop(false)
        }
    };
    window.addEventListener("resize", handleResize);

    const handleSendLead = async (leadData: AnalysisLeadData) => {
        const cookie = getCookie("__exponea_etc__");

        const lead: BloomreachAnalysisLeadModel = {
            firstName: leadData.firstName,
            email: leadData.email,
            phone: leadData.phone,
            campaign: "Adfærdsanalyse",
            source: "Adfærdsanalyse",
            cookie: cookie ?? "",
            situation: situationFromUrl ?? "",
            profiletype: resultTypeFromUrl?.toString() ?? "",
        };

        await postBloomreachAnalysisLead({
            ...lead
        }).then(() => {
            Analytics.getInstance().trackBehaviourProfilingLead();
            setShowLeadModal(false);
            if (leadContainerRef.current) {
                const targetTop = leadContainerRef.current.getBoundingClientRect().top + window.scrollY - 50;
                window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
            }
        });

    };


    return (
        <TopBanner>
            <ResultContainerTop>
                <TypeImgContainer>
                    <TypeImg src={bloomreachItem?.imagePath ?? url} alt={mappedResultType} />
                </TypeImgContainer>
                <div>
                    <TypeTitle>
                        Du skaber <Type ResultType={resultTypeFromUrl}>{mappedResultType}</Type>
                    </TypeTitle>
                    <ResultText dangerouslySetInnerHTML={{ __html: bloomreachItem?.profileDescription ?? "" }} />
                </div>
            </ResultContainerTop>
            <SituationBg>
                <ResultContainer>
                    <Situation ResultType={resultTypeFromUrl}>
                        <SituationTitle>{bloomreachItem?.headline}</SituationTitle>
                        {bloomreachItem?.bodyColumn1 && <SituationText dangerouslySetInnerHTML={{ __html: bloomreachItem.bodyColumn1 }} />}
                        {bloomreachItem?.bodyColumn2 && <SituationText dangerouslySetInnerHTML={{ __html: bloomreachItem.bodyColumn2 }} />}
                        {bloomreachItem?.bodyColumn3 && <SituationText dangerouslySetInnerHTML={{ __html: bloomreachItem.bodyColumn3 }} />}
                    </Situation>
                </ResultContainer>
                <LeadContainer ref={leadContainerRef}>
                    {showLeadModal &&
                        <LeadModal onSendLead={handleSendLead} isDesktop={isDesktop} />
                    }
                    {!showLeadModal &&
                        <ThankYou isDesktop={isDesktop} fromMini={leadSubmittedFromMini} />
                    }
                </LeadContainer>
            </SituationBg>
        </TopBanner>
    );
}